/**
 * Session cache.
 *
 * mm-be (config/settings/base.py, SIMPLE_JWT) issues an access token good for
 * 30 minutes and a refresh token good for 7 days, with no rotation. The web
 * app asks for the refresh token in cookie mode (api/auth-cookie.ts): it lives
 * in an HttpOnly cookie that no script here can read, so it is never stored
 * client-side at all. What is left to keep is the access token.
 *
 * It is held in memory and mirrored into sessionStorage, which dies with the
 * tab and is not readable from another origin, so a reload paints at once
 * instead of waiting on a refresh round trip. Not localStorage: that would put
 * a bearer token on disk, shared across every tab. A new tab, or a tab
 * reopened after the browser restarts, has nothing here and signs itself in
 * from the cookie instead (the store's `ensureSession`).
 */

const KEY = 'mm-session'

export interface StoredSession {
  access: string
}

/**
 * Sessions written before cookie mode also carried `refresh`. It is never
 * read: the cookie is the only refresh credential now. Such a session has no
 * cookie behind it, so it reaches the sign-in page at its next refresh, as an
 * expired one does.
 */
const isSession = (v: unknown): v is StoredSession & { refresh?: unknown } =>
  typeof v === 'object' && v !== null && typeof (v as StoredSession).access === 'string'

export const readSession = (): StoredSession | null => {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isSession(parsed)) return null
    const session = { access: parsed.access }
    // Dropped now rather than at the next sign-in: a week-long credential
    // has no reason to sit in the tab's storage for another half hour.
    if ('refresh' in parsed) writeSession(session)
    return session
  } catch {
    // Private mode, disabled storage, or corrupt JSON. Treat as signed out.
    return null
  }
}

export const writeSession = (session: StoredSession): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ access: session.access }))
  } catch {
    // Non-fatal: the in-memory token still works for this page view.
  }
}

export const clearSession = (): void => {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    /* nothing to clear */
  }
}
